import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { InsightsTraceEntry } from 'contextProviders/InsightsChatProvider/conversationTypes';

// Ghost elements fade in as each reasoning step / tool call arrives, so the
// trace reads as a stream of thoughts appearing one after another rather than
// one line being overwritten by the next.
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 0.7;
    transform: translateY(0);
  }
`;

// Not a chat bubble: a borderless, left-aligned column holding a collapse toggle
// and (when expanded) the faint "ghost" trace that accumulates as the model
// streams.
const Wrapper = styled.div`
  align-self: flex-start;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 4px;
`;

// The collapse toggle — a subtle eyebrow so the (verbose) reasoning stays out of
// the way until a dev opens it. Collapsed by default.
const ToggleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
  opacity: 0.7;
`;

const Chevron = styled.div<{ $expanded: boolean }>`
  display: flex;
  transition: transform 120ms ease;
  transform: rotate(${(props) => (props.$expanded ? '0deg' : '-90deg')});
`;

const GhostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Ghost = styled.div`
  opacity: 0.7;
  animation: ${fadeIn} 160ms ease-out;
  color: var(--color-text-secondary);
`;

// A reasoning block: the model's thinking, streamed token by token (the last
// one keeps growing as deltas land). Whitespace preserved so it reads naturally.
const GhostReasoning = styled(Ghost)`
  font-style: italic;
  white-space: pre-wrap;
`;

// A tool step: a faint marker between reasoning blocks, so the trace shows what
// the agent did (raw tool name — this is a developer view).
const GhostStep = styled(Ghost)`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

// A data-engine call nested under its parent tool (e.g. aggregate_metric →
// query_metrics): indented and lighter so the delegation reads as a sub-step.
const GhostSubStep = styled(GhostStep)`
  margin-left: 16px;
  opacity: 0.55;
`;

const StepName = styled.span`
  font-family: monospace;
`;

const StepArgs = styled.span`
  font-style: italic;
`;

// The reasoning trace (staff-only), shown above an assistant turn both while it
// streams and after the answer lands (it persists — it is the trace, not a
// footer). COLLAPSED BY DEFAULT: the thought elements are verbose, so they stay
// behind a toggle until a dev opens them. Every reasoning block and tool call
// accumulates as its own ghost element in arrival order, nothing replaced by a
// later event. Renders nothing until there's a non-empty step to show.
const InsightsLiveReasoning = ({ trace }: { trace?: InsightsTraceEntry[] }) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const steps = (trace ?? []).filter(
    (entry) => entry.kind !== 'reasoning' || entry.text.trim(),
  );
  if (!steps.length) {
    return null;
  }
  const stepCount = steps.length;
  return (
    <Wrapper data-testid="insights-live-reasoning">
      <ToggleHeader
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        data-testid="insights-reasoning-toggle"
      >
        <Chevron $expanded={expanded}>
          <Icon
            icon="chevron-down"
            width={12}
            height={12}
            color="var(--color-text-secondary)"
          />
        </Chevron>
        <Typography.Body type="Body 12">
          {`Reasoning (${stepCount})`}
        </Typography.Body>
      </ToggleHeader>
      {expanded && (
        <GhostList>
          {steps.map((entry, index) => {
            let node: JSX.Element;
            if (entry.kind === 'reasoning') {
              node = (
                <GhostReasoning>
                  <Typography.Body type="Body 12">{entry.text}</Typography.Body>
                </GhostReasoning>
              );
            } else if (entry.kind === 'context_used') {
              // Not something the model did — what it was given. Listed by
              // title so a staff reader can tell at a glance whether the note
              // they wrote actually reached the answer.
              const { kbVersion, always, triggered, nearMissed } =
                entry.context;
              node = (
                <GhostStep>
                  <Typography.Body type="Body 12">
                    {'▸ Context: '}
                    <StepName>{`KB ${kbVersion ?? 'none'}`}</StepName>
                    {always.length ? (
                      <StepArgs>{` · always: ${always.join(', ')}`}</StepArgs>
                    ) : null}
                    {triggered.length ? (
                      <StepArgs>{` · fired: ${triggered.join(', ')}`}</StepArgs>
                    ) : null}
                    {nearMissed.length ? (
                      <StepArgs>{` · near-missed: ${nearMissed.join(', ')}`}</StepArgs>
                    ) : null}
                  </Typography.Body>
                </GhostStep>
              );
            } else if (entry.kind === 'data_call') {
              node = (
                <GhostSubStep>
                  <Typography.Body type="Body 12">
                    {'↳ '}
                    <StepName>{entry.name}</StepName>
                    {entry.args ? (
                      <StepArgs>{` ${entry.args}`}</StepArgs>
                    ) : null}
                  </Typography.Body>
                </GhostSubStep>
              );
            } else {
              node = (
                <GhostStep>
                  <Typography.Body type="Body 12">
                    {'→ '}
                    <StepName>{entry.name}</StepName>
                  </Typography.Body>
                </GhostStep>
              );
            }
            return <React.Fragment key={index}>{node}</React.Fragment>;
          })}
        </GhostList>
      )}
    </Wrapper>
  );
};

export default InsightsLiveReasoning;
